import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';

import {
    Container,
    Loading,
    SimpleButton,
    ErrorMessage,
    SimpleTextField,
    FilterBarContainer,
} from '@/src/ui';
import { apiRequest } from '@/src/services';
import StandardsTable from './components/StandardsTable';
import StandardsModal from './components/CreateEditStandardsModal';
import DeleteStandardModal from './components/DeleteStandardModal';
import StandardsFilter from './components/StandardsFilter';
import type { StandardDTO } from './models/standardsDTO';
import { useAuth } from '@/src/app/context/useAuth';

const StandardsPage: React.FC = () => {
    document.title = 'Standards';

    const [standards, setStandards] = useState<StandardDTO[]>([]);
    const [gradeOptions, setGradeOptions] = useState<string[]>([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchString, setSearchString] = useState('');

    const { user } = useAuth();
    const userId = user?.id || 'nouser';
    const FILTER_KEY = `standards-filter-${userId}`;

    const [selectedGrade, setSelectedGrade] = useState(() => {
        try {
            const saved = localStorage.getItem(FILTER_KEY);
            if (saved) return saved;
        } catch {
            // intentionally empty
        }
        return '';
    });

    useEffect(() => {
        try {
            localStorage.setItem(FILTER_KEY, selectedGrade);
        } catch {
            // intentionally empty
        }
    }, [selectedGrade, FILTER_KEY]);

    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedStandard, setSelectedStandard] = useState<StandardDTO | null>(null);

    const fetchStandards = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await apiRequest('GET', 'standards');
            if (Array.isArray(response.data)) {
                setStandards(response.data);
            } else if (Array.isArray(response)) {
                setStandards(response);
            } else {
                setStandards([]);
            }

            const res = await apiRequest('GET', 'utility/filterData?getGradeLevelMaster=true');
            const data = res.data as { gradeLevelMasterOptions?: { name: string }[] };
            const options = Array.isArray(data.gradeLevelMasterOptions)
                ? data.gradeLevelMasterOptions.map((g: { name: string }) => g.name)
                : [];
            setGradeOptions(options);
        } catch {
            setError('Failed to load standards or grade options');
            setStandards([]);
            setGradeOptions([]);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchStandards();
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Container>
            <h1 className="page-header">Standards</h1>
            <br />
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                    gap: 2,
                    flexWrap: 'wrap', // allows wrapping on smaller screens
                }}
            >
                {/* Filter + Search on the left */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FilterBarContainer>
                        <StandardsFilter
                            grades={gradeOptions}
                            selectedGrade={selectedGrade}
                            onGradeChange={setSelectedGrade}
                        />
                    </FilterBarContainer>
                    <SimpleTextField
                        label="Search"
                        name="Search"
                        variant="outlined"
                        value={searchString}
                        onChange={(e) => setSearchString(e.target.value)}
                        sx={{ minWidth: 170, maxWidth: 300, mr: 2 }}
                    />
                </Box>

                {/* Add Standard button on the right */}
                <SimpleButton
                    onClick={() => {
                        setSelectedStandard(null);
                        setShowEditModal(true);
                    }}
                    variant="contained"
                    color="primary"
                    className="whitespace-nowrap"
                >
                    Add Standard
                </SimpleButton>
            </Box>

            {error && <ErrorMessage error={error} />}
            {loading ? (
                <Loading />
            ) : (
                <StandardsTable
                    standards={standards.filter((s) => {
                        const search = searchString.trim().toLowerCase();
                        const gradeMatch = !selectedGrade || s.gradeLevelName === selectedGrade;
                        if (!search) return gradeMatch;
                        return (
                            gradeMatch &&
                            (s.name.toLowerCase().includes(search) ||
                                (s.gradeLevelName?.toLowerCase().includes(search) ?? false) ||
                                (s.practiceLessonName?.toLowerCase().includes(search) ?? false))
                        );
                    })}
                    onEdit={(s) => {
                        setSelectedStandard(s);
                        setShowEditModal(true);
                    }}
                    onDelete={(s) => {
                        setSelectedStandard(s);
                        setShowDeleteModal(true);
                    }}
                />
            )}

            {showEditModal && (
                <StandardsModal
                    open={showEditModal}
                    initialData={selectedStandard}
                    onClose={(refresh?: boolean) => {
                        setShowEditModal(false);
                        setSelectedStandard(null);
                        if (refresh) fetchStandards();
                    }}
                />
            )}
            {showDeleteModal && selectedStandard && (
                <DeleteStandardModal
                    open={showDeleteModal}
                    standard={selectedStandard}
                    onClose={() => {
                        setShowDeleteModal(false);
                        setSelectedStandard(null);
                    }}
                    onDeleted={() => {
                        setShowDeleteModal(false);
                        setSelectedStandard(null);
                        fetchStandards();
                    }}
                    setError={setError}
                />
            )}
        </Container>
    );
};

export default StandardsPage;
